import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { cn } from '@/lib/utils';
import { DashboardLayout } from './DashboardLayout';
import { DashboardSkeleton } from './components/SkeletonLoaders';
import { useDashboardData } from './hooks/useDashboardData';
import { calculateMetrics } from './utils/dashboardMetrics';
import { detectUserState, getUserContext } from './utils/userState';
import { getUserCapabilities } from './utils/getUserCapabilities';
import { AddRoleModal } from './modals/AddRoleModal';
import { AddCandidateModal } from './modals/AddCandidateModal';
import { applyStateOverride, getStateOverride } from './utils/devStateOverride';
import { getCurrentEnvironment } from '../../utils/authUtils';
import { DashboardMetrics } from './utils/dashboardTypes';

const Dashboard = () => {
  const { data, inboxItems, loading, error } = useDashboardData();
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [showAddCandidateModal, setShowAddCandidateModal] = useState(false);
  const [, setRefreshTrigger] = useState(0);

  // Show loading state with skeleton
  if (loading) {
    return (
      <div className={cn(
        "grid",
        "grid-cols-[350px_1fr]",
        "grid-rows-[auto_1fr_64px]",
        "max-h-[calc(100vh-60px)] overflow-hidden",
        "m-0 p-0 w-full",
        "font-sans",
        "animate-fade-in motion-reduce:animate-none",
        // Responsive grid
        "xl:grid-cols-[380px_1fr]",
        "2xl:grid-cols-[420px_1fr]"
      )}>
        <Helmet>
          <title>Dashboard | Role Relay</title>
        </Helmet>
        <DashboardSkeleton />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className={cn(
        "grid",
        "grid-cols-[350px_1fr]",
        "grid-rows-[auto_1fr_64px]",
        "max-h-[calc(100vh-60px)] overflow-hidden",
        "m-0 p-0 w-full",
        "font-sans",
        "animate-fade-in motion-reduce:animate-none",
        "xl:grid-cols-[380px_1fr]",
        "2xl:grid-cols-[420px_1fr]"
      )}>
        <Helmet>
          <title>Dashboard | Role Relay</title>
        </Helmet>
        <div className="col-span-full p-8 text-center">
          <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
            Unable to load dashboard
          </h3>
          <p className="text-slate-600 dark:text-slate-400 mb-2">
            There was an error loading your dashboard data. Please try refreshing the page.
          </p>
          <p className="text-slate-500 dark:text-slate-500 text-sm">
            {error.message}
          </p>
        </div>
      </div>
    );
  }

  // Calculate metrics and determine user state
  const metrics = calculateMetrics(data);

  // Apply dev state override in non-production environments
  const isProduction = getCurrentEnvironment() === 'production';
  const devStateOverride = isProduction ? 'auto' : getStateOverride();

  const emptyMetrics: DashboardMetrics = {
    totalRoles: 0,
    openRoles: 0,
    privateRoles: 0,
    publicSharedRoles: 0,
    totalCandidates: 0,
    totalMatches: 0,
    activeMatches: 0,
    newMatches: 0,
    unreadMessages: 0,
    recentRoles: [],
    recentCandidates: [],
    recentMatches: [],
  };

  const simulatedMetrics =
    devStateOverride === 'new_user' || devStateOverride === 'empty_state'
      ? emptyMetrics
      : metrics;

  const simulatedConnections =
    devStateOverride === 'new_user'
      ? []
      : devStateOverride === 'empty_state'
      ? [{ status: 1, provider: 'jobadder' }]
      : data.connections;

  const computedUserState = detectUserState(simulatedMetrics, simulatedConnections);
  const userState = applyStateOverride(computedUserState, isProduction);

  const capabilities = getUserCapabilities(data.userProfile);
  const userContext = getUserContext(data.userProfile, simulatedMetrics, simulatedConnections, capabilities);

  // Handler to refresh when dev state override changes
  const handleDevStateChange = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Modal handlers
  const handleAddRole = () => {
    setShowAddRoleModal(true);
  };

  const handleAddCandidate = () => {
    setShowAddCandidateModal(true);
  };

  const handleModalSuccess = () => {
    // Modals will trigger refetch via refetchQueries
    // No additional action needed
  };

  const handleInviteUser = () => {
    // TODO: Implement InviteCollaboratorModal
    console.log('Invite User clicked');
    // For now, navigate to settings
    window.location.href = '/settings/users';
  };

  const handleConnectBullhorn = () => {
    // Navigate to ATS connection settings with Bullhorn provider
    window.location.href = '/settings/connections?provider=bullhorn';
  };

  const handleConnectJobAdder = () => {
    // Navigate to ATS connection settings with JobAdder provider
    window.location.href = '/settings/connections?provider=jobadder';
  };

  const handleCompleteProfile = () => {
    // Navigate to settings to complete profile
    window.location.href = '/settings';
  };

  const handleSetPreferences = () => {
    // Navigate to settings to set industry preferences
    window.location.href = '/settings?tab=preferences';
  };

  const handleExploreMarketplace = () => {
    // Navigate to public roles to explore marketplace
    window.location.href = '/public-roles';
  };

  const handleTakeTour = () => {
    // TODO: Implement product tour (e.g., using react-joyride)
    console.log('Take tour clicked');
    // For now, navigate to help page or show a modal
    window.location.href = '/help';
  };

  const handleViewHelp = () => {
    // Navigate to help/documentation
    window.location.href = '/help';
  };

  return (
    <div className={cn(
      // CSS Grid layout for horizontal dashboard
      "grid",
      "grid-cols-[350px_1fr]",
      "grid-rows-[auto_1fr_64px]",
      "max-h-[calc(100vh-60px)] overflow-hidden",
      "m-0 p-0 w-full",
      "font-sans",
      "animate-fade-in motion-reduce:animate-none",
      // Responsive breakpoints
      "xl:grid-cols-[380px_1fr]",
      "2xl:grid-cols-[420px_1fr]",
      "[&_*]:min-w-0"  // Prevent grid blowout
    )}>
      <Helmet>
        <title>Dashboard | Role Relay</title>
      </Helmet>

      <DashboardLayout
        data={data}
        metrics={simulatedMetrics}
        userState={userState}
        capabilities={capabilities}
        userContext={userContext}
        inboxItems={inboxItems || []}
        onAddRole={handleAddRole}
        onAddCandidate={handleAddCandidate}
        onInviteUser={handleInviteUser}
        onConnectBullhorn={handleConnectBullhorn}
        onConnectJobAdder={handleConnectJobAdder}
        onCompleteProfile={handleCompleteProfile}
        onSetPreferences={handleSetPreferences}
        onExploreMarketplace={handleExploreMarketplace}
        onTakeTour={handleTakeTour}
        onViewHelp={handleViewHelp}
        onDevStateChange={handleDevStateChange}
        showDevTools={!isProduction}
      />

      {/* Add Role Modal */}
      <AddRoleModal
        isOpen={showAddRoleModal}
        onClose={() => setShowAddRoleModal(false)}
        onSuccess={handleModalSuccess}
      />

      {/* Add Candidate Modal */}
      <AddCandidateModal
        isOpen={showAddCandidateModal}
        onClose={() => setShowAddCandidateModal(false)}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default Dashboard;
